import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { AlertRule } from '@types'
import { alertsApi } from '@api/alerts'

interface AlertRulesState {
  alertRules: AlertRule[]
  selectedAlertRule: AlertRule | null
  loadingState: 'idle' | 'pending' | 'succeeded' | 'failed'
  error: string | null
}

const initialState: AlertRulesState = {
  alertRules: [],
  selectedAlertRule: null,
  loadingState: 'idle',
  error: null,
}

// Async thunks for Alert Rules
export const fetchAlertRules = createAsyncThunk(
  'alertRules/fetchAlertRules',
  async (_, { rejectWithValue }) => {
    try {
      const response = await alertsApi.getAlertRules()
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch alert rules')
    }
  }
)

export const fetchAlertRule = createAsyncThunk(
  'alertRules/fetchAlertRule',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await alertsApi.getAlertRule(id)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch alert rule')
    }
  }
)

export const createAlertRule = createAsyncThunk(
  'alertRules/createAlertRule',
  async (data: Partial<AlertRule>, { rejectWithValue }) => {
    try {
      const response = await alertsApi.createAlertRule(data)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create alert rule')
    }
  }
)

export const updateAlertRule = createAsyncThunk(
  'alertRules/updateAlertRule',
  async ({ id, data }: { id: number; data: Partial<AlertRule> }, { rejectWithValue }) => {
    try {
      const response = await alertsApi.updateAlertRule(id, data)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update alert rule')
    }
  }
)

export const deleteAlertRule = createAsyncThunk(
  'alertRules/deleteAlertRule',
  async (id: number, { rejectWithValue }) => {
    try {
      await alertsApi.deleteAlertRule(id)
      return id
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete alert rule')
    }
  }
)

const alertRulesSlice = createSlice({
  name: 'alertRules',
  initialState,
  reducers: {
    clearSelectedAlertRule: (state) => {
      state.selectedAlertRule = null
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all alert rules
      .addCase(fetchAlertRules.pending, (state) => {
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(fetchAlertRules.fulfilled, (state, action) => {
        state.loadingState = 'succeeded'
        state.alertRules = Array.isArray(action.payload?.data) ? action.payload.data : []
      })
      .addCase(fetchAlertRules.rejected, (state, action) => {
        state.loadingState = 'failed'
        state.error = action.payload as string
      })

      // Fetch single alert rule
      .addCase(fetchAlertRule.pending, (state) => {
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(fetchAlertRule.fulfilled, (state, action) => {
        state.loadingState = 'succeeded'
        state.selectedAlertRule = action.payload.data
      })
      .addCase(fetchAlertRule.rejected, (state, action) => {
        state.loadingState = 'failed'
        state.error = action.payload as string
      })

      // Create alert rule
      .addCase(createAlertRule.pending, (state) => {
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(createAlertRule.fulfilled, (state, action) => {
        state.loadingState = 'succeeded'
        state.alertRules.push(action.payload.data)
      })
      .addCase(createAlertRule.rejected, (state, action) => {
        state.loadingState = 'failed'
        state.error = action.payload as string
      })

      // Update alert rule
      .addCase(updateAlertRule.pending, (state) => {
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(updateAlertRule.fulfilled, (state, action) => {
        state.loadingState = 'succeeded'
        const index = state.alertRules.findIndex((rule: AlertRule) => rule.id === action.payload.data.id)
        if (index !== -1) {
          state.alertRules[index] = action.payload.data
        }
        if (state.selectedAlertRule?.id === action.payload.data.id) {
          state.selectedAlertRule = action.payload.data
        }
      })
      .addCase(updateAlertRule.rejected, (state, action) => {
        state.loadingState = 'failed'
        state.error = action.payload as string
      })

      // Delete alert rule
      .addCase(deleteAlertRule.pending, (state) => {
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(deleteAlertRule.fulfilled, (state, action) => {
        state.loadingState = 'succeeded'
        state.alertRules = state.alertRules.filter((rule: AlertRule) => rule.id !== action.payload)
        if (state.selectedAlertRule?.id === action.payload) {
          state.selectedAlertRule = null
        }
      })
      .addCase(deleteAlertRule.rejected, (state, action) => {
        state.loadingState = 'failed'
        state.error = action.payload as string
      })
  },
})

export const { clearSelectedAlertRule, clearError } = alertRulesSlice.actions
export default alertRulesSlice.reducer
